import _ from 'lodash';
import { CreateArchiveSpecification } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/create-archive-specification';
import { IDestination } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/destination';
import { Job } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/job';
import { UploadJob } from '@wix/media-platform-js-client/dist/mjs/src/public/platform/uploader/upload-job';

import { THEME } from '../../../constants/notification';
import { loadMoreFiles } from '../../../redux/file-manager-component/file-manager-component.actions';
import { FILE_MANAGER_PAGE_COMPONENT_ID } from '../../../redux/file-manager-component/file-manager-component.types';
import { updateFileACLInternal } from '../../../redux/files/file-acl-update.actions';
import {
  checkIfFileExists,
  checkIfFolderExist,
  createFileError,
  createFileSuccess,
  creatingFileSuccess,
  deleteFileByPath,
  deleteFilesByIds,
  getRequestParams,
  uploadFileProgress,
  uploadFilesGroupStart,
  uploadFileSuccess,
} from '../../../redux/files/files.actions';
import {
  getFilePathById,
  getMediaAssetsMap,
} from '../../../redux/files/files.selectors';
import { JobStatus, JobTypes } from '../../../redux/jobs/jobs.types';
import {
  getCurrentFileService,
  getDefaultProjectId,
  getProjectByRoute,
} from '../../../redux/projects/projects.selectors';
import { Dispatch, GetState, ThunkAction } from '../../../redux/redux.types';
import { getSelectedOrganizationId } from '../../../selectors/organizations/organizations';
import {
  createArchive,
  DEFAULT_ARCHIVE_ACL,
} from '../../../transport/services/archive';
import { mediaAssetsService } from '../../../transport/services/media-assets.service';
import { ACL, FileDescriptor, Project } from '../../../types/entities';
import { UploadFilesParams } from '../../../types/files';
import {
  LoadFilesRequestData,
  MediaAsset,
  UploadSuccessEventData,
} from '../../../types/media-assets';
import { MaybeUndef } from '../../../types/system';
import { i18n } from '../../../utils';
import { copyToClipboard } from '../../../utils/copy-to-clipboard/copy-to-clipboard';
import { errorLogger } from '../../../utils/error-logger/error-logger';
import { normalizeFile } from '../../../utils/media-assets/media-assets';
import { getParentFolder } from '../../../utils/path/path';
import { delayPromise } from '../../../utils/promise';
import { BI_ERRORS } from '../../../utils/statistics/bi-error-logger/bi-errors';
import { closeOperationGroup } from '../../operation-panel/operation-groups/operation-groups.actions';
import {
  JobOperationContext,
  OperationGroupType,
} from '../../operation-panel/operation-groups/operation-groups.types';
import { goToFileManager } from '../../permissions/navigation';
import { startCompressFlow } from '../compress-flow/compress-flow.actions';
import { generateFilePathLink } from '../file-info/file-info-utils/file-info-utils';
import { FileAction } from '../file-manager.types';
import { FileActionPayload } from '../files-list/menu-actions-tooltip/menu-actions/menu-actions';

import {
  getCurrentPath,
  getSelectedFileIds,
  getSelectedFiles,
  isAlreadyDownloadingBatch,
} from './file-manager.selectors';

export const FILE_MANAGER_ACTIONS = {
  FILE_DOWNLOAD_ERROR: 'MEDIA_ASSETS.FILE.DOWNLOAD.ERROR',
  DOWNLOAD_MULTIPLE_START: 'MEDIA_ASSETS_ACTIONS__DOWNLOAD_MULTIPLE_START',

  NAVIGATE_TO_FILE_IN_CURRENT_FOLDER:
    'FILE_MANAGER_ACTIONS.NAVIGATE_TO_FILE_IN_CURRENT_FOLDER',
  OPEN_EXPAND_MODE: 'FILE_MANAGER_ACTIONS.OPEN_EXPAND_MODE',
  CLOSE_EXPAND_MODE: 'FILE_MANAGER_ACTIONS.CLOSE_EXPAND_MODE',

  OPEN_DELETE_MODAL: 'FILE_MANAGER_ACTIONS.OPEN_DELETE_MODAL',
  CLOSE_DELETE_MODAL: 'FILE_MANAGER_ACTIONS.CLOSE_DELETE_MODAL',

  SHOW_NOTIFICATION_AFTER_FILE_NAME_COPIED:
    'MEDIA_ASSETS_ACTIONS__SHOW_NOTIFICATION_AFTER_FILE_NAME_COPIED',

  SHOW_NOTIFICATION_AFTER_FOLDER_NAME_COPIED:
    'MEDIA_ASSETS_ACTIONS__SHOW_NOTIFICATION_AFTER_FOLDER_NAME_COPIED',

  SHOW_NOTIFICATION_AFTER_FILE_URL_LINK_COPIED:
    'MEDIA_ASSETS_ACTIONS__SHOW_NOTIFICATION_AFTER_FILE_URL_LINK_COPIED',
};

const navigateToFileInCurrentFolder = (fileId: string) => ({
  type: FILE_MANAGER_ACTIONS.NAVIGATE_TO_FILE_IN_CURRENT_FOLDER,
  payload: fileId,
});

export const locateFile =
  (fileId: string) =>
  (dispatch: Dispatch, getState: GetState): void => {
    const state = getState();
    const file: MaybeUndef<MediaAsset> = getMediaAssetsMap(state)[fileId];

    if (!file) {
      // file is not found, exit
      return;
    }

    const organizationId = getSelectedOrganizationId(state);
    const projectId = getDefaultProjectId(state);
    const currentPath = getCurrentPath(state);
    const parentFolder = getParentFolder(file.path);

    if (currentPath === parentFolder) {
      dispatch(navigateToFileInCurrentFolder(fileId));
    }

    goToFileManager(organizationId, projectId, parentFolder, fileId);
  };

export const openExpandMode = (fileId: string) => ({
  type: FILE_MANAGER_ACTIONS.OPEN_EXPAND_MODE,
  payload: fileId,
});

export const closeExpandMode = () => ({
  type: FILE_MANAGER_ACTIONS.CLOSE_EXPAND_MODE,
  payload: undefined,
});

export const deleteCurrentFolder =
  () =>
  (dispatch: Dispatch, getState: GetState): void => {
    const state = getState();
    const { organizationId, project } = getRequestParams(state);
    const path = getCurrentPath(state);

    deleteFileByPath(path, true)(dispatch, getState).then(() => {
      const parentPath = getParentFolder(path);
      goToFileManager(organizationId, project.id, parentPath);
    });
  };

export const openFileDeleteModal = () => ({
  type: FILE_MANAGER_ACTIONS.OPEN_DELETE_MODAL,
  payload: undefined,
});

export const closeFileDeleteModal = () => ({
  type: FILE_MANAGER_ACTIONS.CLOSE_DELETE_MODAL,
  payload: undefined,
});

const downloadFileError = (reason) => ({
  type: FILE_MANAGER_ACTIONS.FILE_DOWNLOAD_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.media-assets.file.download.error'),
    theme: THEME.ERROR,
  },
});

export const downloadFile =
  (path: string): ThunkAction<Promise<void>> =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const requestParams = getRequestParams(state);
    const { project, organizationId } = requestParams;

    return mediaAssetsService
      .downloadFile({ path, project, organizationId })
      .catch((response) => {
        errorLogger.logError({
          errorDescription: BI_ERRORS.fileManager.getDownloadUrlRequestFailed,
          otherParams: { response },
        });
        dispatch(downloadFileError(_.get(response, 'data')));
      });
  };

export const goToFolderIfExists = (
  params: LoadFilesRequestData,
): Promise<void> => {
  const { path, organizationId, project } = params;

  return checkIfFolderExist(params).then(({ files }) => {
    if (!files.length) {
      return checkIfFileExists({ path, organizationId, project }).then(
        (file) => {
          return goToFileManager(
            organizationId,
            project.id,
            getParentFolder(path),
            file.id,
          );
        },
      );
    }

    return goToFileManager(organizationId, project.id, path);
  });
};

export const FILE_MANAGER_SORTING_INFO_ID = FILE_MANAGER_PAGE_COMPONENT_ID;

export interface MultipleDownloadPayload {
  batchClientId: string;
  sourceFileIds: string[];
}

const downloadMultipleStart = (payload: MultipleDownloadPayload) => ({
  type: FILE_MANAGER_ACTIONS.DOWNLOAD_MULTIPLE_START,
  payload,
});

const CLOSE_DOWNLOAD_GROUP_DELAY = 5000;

const downloadMultiple =
  (fileIds: string[]) =>
  (dispatch: Dispatch, getState: GetState): void => {
    const state = getState();
    const currentPath: string = getCurrentPath(state);
    const requestParams = getRequestParams(state);
    const organizationId: string = getSelectedOrganizationId(state);
    const project: Project = getProjectByRoute(state);
    const batchClientId = _.uniqueId('operationPanelGroupId_');
    const isAlreadyDownloadingSameFiles = isAlreadyDownloadingBatch(
      state,
      fileIds,
    );

    if (isAlreadyDownloadingSameFiles) {
      return;
    }

    const jobOperationContext: JobOperationContext = {
      operationType: OperationGroupType.MultipleDownload,
      groupId: batchClientId,
    };
    const destination: IDestination = {
      directory: currentPath,
      acl: DEFAULT_ARCHIVE_ACL,
    };

    dispatch(downloadMultipleStart({ batchClientId, sourceFileIds: fileIds }));

    dispatch({
      initiateJob: (): Promise<Job<CreateArchiveSpecification>> =>
        createArchive(organizationId, project, destination, fileIds),
      context: jobOperationContext,
    }).then(({ job }) => {
      if (
        job.status !== JobStatus.SUCCESS ||
        !job.result ||
        job.type !== JobTypes.ArchiveCreate
      ) {
        return;
      }

      mediaAssetsService
        .downloadFile({ path: job.result.payload.path, ...requestParams })
        .then(() => delayPromise(CLOSE_DOWNLOAD_GROUP_DELAY))
        .then(() => {
          dispatch(closeOperationGroup(batchClientId, []));
        });
    });
  };

export const downloadSelection =
  () =>
  (dispatch: Dispatch, getState: GetState): void => {
    const state = getState();
    const fileIds: string[] = getSelectedFileIds(state);

    if (fileIds.length === 1) {
      const filePath = getFilePathById(state, fileIds[0]);

      if (filePath) {
        downloadFile(filePath)(dispatch, getState);
      }

      return;
    }

    downloadMultiple(fileIds)(dispatch, getState);
  };

export const updateFileACL =
  (acl: ACL) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const selectedFileIds = getSelectedFileIds(state);
    const { organizationId, project } = getRequestParams(state);

    _.forEach(selectedFileIds, (id: string) =>
      dispatch(updateFileACLInternal({ acl, id, organizationId, project })),
    );
  };

export const execFileAction =
  (fileActionPayload: FileActionPayload) =>
  (dispatch: Dispatch, getState: GetState): void => {
    const state = getState();
    const selectedFiles = getSelectedFiles(state);
    const fileService = getCurrentFileService(state);

    const item = selectedFiles[0];

    switch (fileActionPayload.fileAction) {
      case FileAction.UpdateACLToPublic:
        dispatch(updateFileACL(ACL.PUBLIC));
        return;

      case FileAction.UpdateACLToPrivate:
        dispatch(updateFileACL(ACL.PRIVATE));
        return;

      case FileAction.CopyName:
        copyToClipboard(item.clientName);

        if (item.isFolder) {
          dispatch(showNotificationAfterFolderNameCopied());
        } else {
          dispatch(showNotificationAfterFileNameCopied());
        }

        return;

      case FileAction.CopyUrlLink:
        copyToClipboard(generateFilePathLink(fileService, selectedFiles[0]));
        dispatch(showNotificationAfterFileUrlLinkCopied());
        return;

      case FileAction.Delete:
        dispatch(openFileDeleteModal());
        return;

      case FileAction.Download:
        dispatch(downloadSelection());
        return;

      case FileAction.Compress:
        dispatch(startCompressFlow());
        return;

      default:
        // do nothing or maybe log an error
        return;
    }
  };

export const showNotificationAfterFileNameCopied = () => ({
  type: FILE_MANAGER_ACTIONS.SHOW_NOTIFICATION_AFTER_FILE_URL_LINK_COPIED,
  payload: undefined,
  notification: {
    message: i18n.t('notifications.media-assets.file.name-copied.success'),
    theme: THEME.STANDARD,
  },
});

export const showNotificationAfterFolderNameCopied = () => ({
  type: FILE_MANAGER_ACTIONS.SHOW_NOTIFICATION_AFTER_FILE_URL_LINK_COPIED,
  payload: undefined,
  notification: {
    message: i18n.t('notifications.media-assets.folder.name-copied.success'),
    theme: THEME.STANDARD,
  },
});

const showNotificationAfterFileUrlLinkCopiedAction = () => ({
  type: FILE_MANAGER_ACTIONS.SHOW_NOTIFICATION_AFTER_FILE_URL_LINK_COPIED,
  payload: undefined,
  notification: {
    message: i18n.t('notifications.media-assets.file.url-link-copied.success'),
    theme: THEME.STANDARD,
  },
});

export const showNotificationAfterFileUrlLinkCopied =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(showNotificationAfterFileUrlLinkCopiedAction());
  };

export const loadMoreFilesForCurrentPath =
  () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const project = getProjectByRoute(state);

    loadMoreFiles(
      {
        project,
        path: getCurrentPath(state),
        organizationId: getSelectedOrganizationId(state),
      },
      FILE_MANAGER_PAGE_COMPONENT_ID,
    )(dispatch, getState);
  };

export const uploadFileInternal =
  (path: string, clientId: string, file: File) =>
  (dispatch: Dispatch, getState: GetState): UploadJob => {
    const state = getState();

    dispatch(creatingFileSuccess({ id: clientId, file, path }));

    const requestParams = getRequestParams(state);
    const { project, organizationId } = requestParams;

    return mediaAssetsService
      .uploadFile({ path, file, project, organizationId })
      .on('upload-error', (response) => {
        dispatch(createFileError({ ...response, clientId, file }));
      })
      .on('upload-aborted', (response) => {
        dispatch(createFileError({ ...response, clientId, file }));
      })
      .on('upload-progress', (response) => {
        dispatch(uploadFileProgress({ ...response, clientId }));
      })
      .on('upload-success', ({ fileDescriptors }: UploadSuccessEventData) => {
        const state = getState();
        const fileRaw: FileDescriptor = fileDescriptors[0];
        const file = normalizeFile(fileRaw);

        if (getParentFolder(file.path) !== getCurrentPath(state)) {
          return dispatch(
            uploadFileSuccess({ clientId, file, parentFolderPath: path }),
          );
        }

        dispatch(createFileSuccess({ clientId, file, parentFolderPath: path }));
      });
  };

export const uploadFile =
  (path: string, file: File) => (dispatch: Dispatch, getState: GetState) => {
    return uploadFileInternal(
      path,
      _.uniqueId('operationPanelClientId_'),
      file,
    )(dispatch, getState);
  };

export const uploadFiles =
  ({ files, path }: UploadFilesParams) =>
  (dispatch: Dispatch, getState: GetState) => {
    const newFileClientIds: string[] = [];

    _.forEach(files, (file: File) => {
      const newClientId = _.uniqueId('operationPanelClientId_');
      newFileClientIds.push(newClientId);

      uploadFileInternal(path, newClientId, file)(dispatch, getState);
    });

    dispatch(uploadFilesGroupStart(newFileClientIds));
  };

export const deleteSelectedFiles =
  (): ThunkAction<void> => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const selectedFileIds = getSelectedFileIds(state);
    return dispatch(deleteFilesByIds(selectedFileIds));
  };
